/**
 * PDP v2 set row API (019 §8, S2), loaded after pdp-v2.js (uses theme.pdpV2)
 * and before pdp-v2-set.js (the elements); split out for the §11 budget
 * (plan 001 A6). The quote maths (CO.computeState / CO.unitSaving), the
 * drawer-section preflight and the set-add owner: one POST, never retried;
 * only a 2xx with items confirms; tracking after a confirmed POST is
 * independent of rendering.
 */
(function () {
  'use strict';

  var theme = window.theme = window.theme || {};
  var api = theme.pdpV2;
  if (!api || api.__setApiInitialised) return;
  api.__setApiInitialised = true;

  var coApi = api.coApi = function () { return theme.collectionOffers || null; };

  /**
   * 002-F1: the offer while Collection Offer state still lists its slot as
   * live, else null. CO.getOffers drops a slot at its end, also on a page
   * cached or left open past it, and never lists a slot this page's offer
   * config did not carry. Fails closed: no module, or one without getOffers.
   * Every offer calculation starts here; slot 0 is the plain colours row.
   */
  var liveOffer = api.liveOffer = function (co, offer) {
    if (!co || !offer || typeof co.getOffers !== 'function' || !(Number(offer.slot) > 0)) return null;
    var slot = Number(offer.slot);
    return co.getOffers().some(function (o) { return Number(o.slot) === slot; }) ? offer : null;
  };

  /**
   * §8.4 prospective qualification: offer {slot, percentage, min}, CO cart lines, units [{price, compareAt}] per new line.
   * No live offer (ended, or the plain colours row): plain prices. bagSaving is what the bag's own lines start saving.
   */
  api.setQuote = function (co, offer, cartLines, units) {
    if (!liveOffer(co, offer)) {
      var plain = units.map(function (u) {
        var p = Number(u.price) || 0;
        return { price: p, full: Math.max(p, Number(u.compareAt) || 0), saving: 0, net: p };
      });
      return { qualified: false, remaining: 0, rows: plain, total: plain.reduce(function (n, r) { return n + r.net; }, 0), was: null, save: 0, bagDrops: false, bagSaving: 0 };
    }
    var slot = Number(offer.slot);
    var base = (cartLines || []).slice();
    var added = units.map(function (u) { return { slot: slot, quantity: 1, price: u.price, compareAt: u.compareAt || 0 }; });
    var after = co.computeState([offer], base.concat(added))[slot];
    var before = co.computeState([offer], base)[slot];
    var rows = units.map(function (u) {
      var full = Math.max(Number(u.price) || 0, Number(u.compareAt) || 0);
      var saving = after.qualified ? co.unitSaving(u.price, u.compareAt, offer.percentage) : 0;
      return { price: Number(u.price) || 0, full: full, saving: saving, net: (Number(u.price) || 0) - saving };
    });
    var total = rows.reduce(function (n, r) { return n + r.net; }, 0);
    var was = rows.reduce(function (n, r) { return n + r.full; }, 0);
    return {
      qualified: after.qualified,
      remaining: after.remaining,
      rows: rows,
      total: total,
      was: after.qualified ? was : null,
      save: after.qualified ? was - total : 0,
      bagDrops: after.qualified && !before.qualified && before.existingSaving > 0,
      bagSaving: after.qualified && !before.qualified ? before.existingSaving : 0
    };
  };

  /** §8.4 steps 5 and 6: the pant's set price, and whether the bag plus the pant qualifies (no live offer: no saving). */
  api.pantQuote = function (co, offer, cartLines, pant) {
    if (!liveOffer(co, offer)) return { saving: 0, setPrice: Number(pant.price) || 0, bagQualifies: false };
    var slot = Number(offer.slot);
    var saving = co.unitSaving(pant.price, pant.compareAt, offer.percentage);
    var lines = (cartLines || []).concat([{ slot: slot, quantity: 1, price: pant.price, compareAt: pant.compareAt || 0 }]);
    var withBag = co.computeState([offer], lines)[slot];
    var bagHasSlot = (cartLines || []).some(function (l) { return Number(l.slot) === slot && !l.giftCard && l.quantity > 0; });
    return { saving: saving, setPrice: pant.price - saving, bagQualifies: withBag.qualified && bagHasSlot };
  };

  /** §8.7 drawer-section preflight: hardening 010 §B2, both predicates. */
  api.drawerSectionValid = function (html, liveForm) {
    if (!html || typeof html !== 'string') return false;
    var t = document.createElement('template');
    t.innerHTML = html;
    if (!t.content.querySelector('[data-merge-list="cart-items"]')) return false;
    if (liveForm && liveForm.querySelector('[data-merge="collection-offer-state"]')
      && !t.content.querySelector('[data-merge="collection-offer-state"]')) return false;
    return true;
  };

  var fill = function (template, vars) {
    return String(template || '')
      .replace(/\[(\w+)\]/g, function (m, k) { return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m; })
      .replace(/\{(\w+)\}/g, function (m, k) { return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m; });
  };
  api.fill = fill;

  /**
   * A2: a colour's variant in the size chosen on the main product (matched on
   * the size option's value; the pieces island carries each variant's
   * option1). No size yet: its first available variant. ok = can be added.
   */
  api.sizeVariant = function (piece, size) {
    var vs = (piece && piece.variants) || [];
    if (size === null || size === undefined) {
      var first = vs.find(function (x) { return x.available; }) || vs[0] || null;
      return { variant: first, ok: !!(first && first.available) };
    }
    var v = vs.find(function (x) { return String(x.option) === String(size); }) || null;
    return { variant: v, ok: !!(v && v.available) };
  };

  /**
   * A4: the free-shipping bar's bag total and standard threshold, read from
   * the same attributes as pdp-v2-delivery.js; null without a bar.
   */
  api.shippingBar = function () {
    var bar = document.querySelector('.free-shipping-bar[data-free-shipping-current-cart]');
    if (!bar) return null;
    var bag = Number(bar.getAttribute('data-free-shipping-current-cart'));
    var threshold = Number(bar.getAttribute('data-free-shipping-amount'));
    return isFinite(bag) && threshold > 0 ? { bag: bag, threshold: threshold } : null;
  };

  /** A4: true when the bag after this add (its net total, less what the bag's own lines start saving) newly clears free shipping. */
  api.crossesFreeShipping = function (bar, quote) {
    if (!bar || !quote || quote.unknown) return false;
    return bar.bag < bar.threshold && bar.bag + quote.total - (quote.bagSaving || 0) >= bar.threshold;
  };

  /**
   * The set POST's lines, mirroring CO.addBody exactly (plan 001 E4): the
   * hidden `_darmha_offer` "<surface>/<slot>" on every line except one marked
   * `untagged` (the main product, so it never splits from the same variant
   * already in the bag). The plain colours row builds with this copy:
   * collection-offer.js loads only while an offer is live (002-F2).
   */
  api.lineBody = function (items, surface, slot) {
    var tag = surface + '/' + (Number(slot) || 0);
    return (items || []).map(function (item) {
      var line = { id: Number(item.variantId), quantity: Math.max(1, Math.floor(Number(item.quantity) || 1)) };
      if (!item.untagged) line.properties = { _darmha_offer: tag };
      return line;
    });
  };

  // ---- the mutation owner: module-scoped, outlives row disconnects (§8.8) ----
  var mutation = api.mutation = api.mutation || { pending: null, lastOutcome: null };

  function sectionTargets() {
    var out = [];
    ['page-header', 'cart-drawer'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el && el.dataset && el.dataset.sectionId) out.push(el);
    });
    return out;
  }

  function refreshHeader() {
    var header = document.querySelector('page-header');
    if (!header || !header.dataset.sectionId) return Promise.resolve();
    var id = header.dataset.sectionId;
    return fetch(location.pathname + '?sections=' + encodeURIComponent(id))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && data[id] && typeof header.updateFromCartChange === 'function') header.updateFromCartChange(data[id]);
      })
      .catch(function () {});
  }
  api.refreshHeader = refreshHeader;

  // §8.7 opener order: the set button if connected, a connected row's, the bag link, null.
  function liveOpener(o) {
    return (o && o.isConnected && o) || document.querySelector('pdp-set-row [data-cta]')
      || document.querySelector('page-header a[href$="/cart"]') || null;
  }

  function formatUnit(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }
  api.formatUnit = formatUnit;

  /**
   * §8.9 tracking after a confirmed add, independent of rendering (017 Q1).
   * pdp_add counts every line, source pdp_set (pdp_colours for the plain
   * row). offer_add (plan 001 E5) counts the lines the surface tagged, as
   * CO.addItems does: the main line is untagged. Klaviyo: one event per line.
   */
  function trackSet(items, currency, meta) {
    var m = meta || {};
    var sum = function (list) { return list.reduce(function (n, i) { return n + i.price; }, 0) / 100; };
    api.track('pdp_add', { source: m.source || 'pdp_set', items_count: items.length, value: sum(items), currency: currency });
    if (m.surface) {
      var tagged = items.filter(function (i) { return !i.untagged; });
      api.track('offer_add', { source: m.surface, slot: Number(m.slot) || 0, items_count: tagged.length, value: sum(tagged), currency: currency });
    }
    var learnq = window._learnq;
    if (!learnq || typeof learnq.push !== 'function') return;
    fetch(location.origin + '/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      items.forEach(function (i) {
        learnq.push(['track', 'Added to Cart', Object.assign({
          total_price: cart.total_price / 100,
          $value: cart.total_price / 100,
          total_discount: cart.total_discount,
          original_total_price: cart.original_total_price / 100,
          items: cart.items
        }, {
          Name: i.title,
          ProductID: i.productId,
          Categories: i.type ? [i.type] : [],
          ImageURL: i.image || '',
          URL: location.origin + i.url,
          Brand: i.vendor,
          Price: formatUnit(i.price, currency),
          CompareAtPrice: formatUnit(i.compareAt || 0, currency),
          $use_ip: true,
          $is_session_activity: true
        })]);
      });
    }).catch(function () {});
  }
  api.trackSet = trackSet;

  /**
   * §8.7: resolves {kind: 'confirmed'} or {kind: 'unproven', message}; later failures never make it unproven.
   * opts.surface (set_pair | set_colours | colours) and opts.slot tag the lines (E4): CO.addBody for an
   * offer row, this file's mirror for the plain row (opts.plain), which never needs the module.
   */
  api.addSet = function (opts) {
    if (mutation.pending) return mutation.pending;
    mutation.lastOutcome = null;
    var targets = sectionTargets();
    var surface = opts.surface || 'set_pair';
    var co0 = coApi();
    var lines = !opts.plain && co0 && typeof co0.addBody === 'function' ? co0.addBody : api.lineBody;
    var body = {
      items: lines(opts.items, surface, opts.slot),
      sections: targets.map(function (el) { return el.dataset.sectionId; }).join(','),
      sections_url: location.pathname
    };
    var settle = fetch(theme.routes.cartAdd + '?pdp_source=set', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
      keepalive: true
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = JSON.parse(text); } catch (e) { data = null; }
        return { ok: res.ok, data: data };
      }, function () { return { ok: false, data: null }; });
    }, function () {
      return { ok: false, data: null };
    }).then(function (r) {
      // §8.8: complete against the live document; sections may have been replaced meanwhile.
      var drawer = document.querySelector('cart-drawer');
      var header = document.querySelector('page-header');
      var confirmed = r.ok && r.data && Array.isArray(r.data.items);
      if (!confirmed) {
        var message = (r.data && typeof r.data.description === 'string' && r.data.description) || opts.copy.unproven;
        mutation.lastOutcome = { kind: 'unproven', message: message, dismissed: false };
        if (drawer) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: liveOpener(opts.opener) } }));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        } else {
          var co = coApi();
          if (co && typeof co.refresh === 'function') co.refresh();
          if (header) refreshHeader();
        }
        return { kind: 'unproven', message: message };
      }
      try { trackSet(opts.items, opts.currency, { source: opts.source, surface: surface, slot: opts.slot }); } catch (e) { /* tracking never blocks */ }
      mutation.returnFocus = true; // pdp-v2.js returns focus to the row when the drawer closes
      var sections = r.data.sections || {};
      try {
        if (drawer) {
          var dHtml = sections[drawer.dataset.sectionId];
          if (api.drawerSectionValid(dHtml, drawer.querySelector('cart-form'))) {
            drawer.updateFromCartChange(dHtml);
          } else {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          }
        } else {
          var co2 = coApi();
          if (co2 && typeof co2.refresh === 'function') co2.refresh();
        }
        if (header) {
          var hHtml = sections[header.dataset.sectionId];
          if (hHtml && typeof header.updateFromCartChange === 'function') header.updateFromCartChange(hHtml);
          else refreshHeader();
        }
        if (drawer && theme.settings && theme.settings.afterAddToCart === 'drawer' && !drawer.hasAttribute('open')) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: liveOpener(opts.opener) } }));
        }
      } catch (e) { /* a rendering failure after a confirmed POST is not an unproven add */ }
      return { kind: 'confirmed' };
    }).then(function (outcome) {
      mutation.pending = null;
      return outcome;
    }, function () {
      mutation.pending = null;
      return { kind: 'unproven', message: opts.copy.unproven };
    });
    mutation.pending = settle;
    return settle;
  };
})();
